import * as React from 'react';
import moment from 'moment';
import { Tournament } from 'Src/models/tournament';
import {
  TOURNAMENT_SUBTYPE_SERVER_TO_CLIENT_MAPPING,
  TOURNAMENT_TYPE_SERVER_TO_CLIENT_MAPPING
} from 'Src/consts/tournament';
import { getTournamentCapacitySummary } from 'Src/helpers/tournament/tournament';

const getSportColor = (key: string): string => {
  const hash = String(key || 'sport')
    .split('')
    .reduce((value, char) => ((value << 5) - value + char.charCodeAt(0)) | 0, 0);
  return `hsl(${Math.abs(hash) % 360}, 58%, 47%)`;
};

const getTournamentType = (tournament: Tournament): string =>
  TOURNAMENT_TYPE_SERVER_TO_CLIENT_MAPPING[tournament.type] || tournament.type || 'Type not set';

const getTournamentSubtype = (tournament: Tournament): string =>
  TOURNAMENT_SUBTYPE_SERVER_TO_CLIENT_MAPPING[tournament.subType] || tournament.subType || '';

export const TournamentNameCell = (tournament: Tournament): React.ReactElement => {
  const sportName = tournament.sport?.name || 'Sport not set';
  return (
    <td>
      <div className="utListTournament">
        <div className="utListTournament__title">
          {tournament.name}
          {!!tournament.readyToConfirmGroupCount && (
            <span className="utListBadge is-warning">
              {tournament.readyToConfirmGroupCount} {tournament.readyToConfirmGroupCount === 1 ? 'group' : 'groups'} ready to confirm
            </span>
          )}
          {tournament.hasUnansweredConfirmationRequests ? (
            <span
              className="utListFlag"
              title="Unanswered confirmation requests"
              aria-label="Unanswered confirmation requests"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 21V4m0 1c3-2 6 2 10 0 1.5-.75 2.7-.9 4-.55v10c-1.3-.35-2.5-.2-4 .55-4 2-7-2-10 0" />
              </svg>
            </span>
          ) : null}
          {tournament.isAutoEnrollmentToWaitingList ? <span className="utListBadge is-warning">WL only</span> : null}
          {tournament.isPublishOnTheWebsite ? (
            <span className="utListStateIcon is-published" title="Published on the public website" aria-label="Published on the public website">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M3 12h18M12 3c3 3.3 3 14.7 0 18M12 3c-3 3.3-3 14.7 0 18" />
              </svg>
            </span>
          ) : null}
          {tournament.isPromoteTournament ? (
            <span className="utListStateIcon is-highlighted" title="Highlighted tournament" aria-label="Highlighted tournament">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-2.9-5.6 2.9 1.1-6.2L3 9.6l6.2-.9L12 3Z" />
              </svg>
            </span>
          ) : null}
        </div>
        <div className="utListTournament__meta">
          <i style={{ backgroundColor: getSportColor(tournament.sport?.id || tournament.sportId || sportName) }} />
          <span>{sportName}</span>
          <b>·</b>
          <span>{getTournamentType(tournament)}</span>
        </div>
      </div>
    </td>
  );
};

export const TournamentStartCell = (tournament: Tournament): React.ReactElement => {
  const start = moment(tournament.startTime);
  const end = moment(tournament.endTime);
  const isSameDay = start.isSame(end, 'day');

  return (
    <td>
      <div className="utListDate">
        <strong>{start.format('DD MMM YYYY')}</strong>
        <span>{isSameDay ? `${start.format('HH:mm')}–${end.format('HH:mm')}` : start.format('HH:mm')}</span>
      </div>
    </td>
  );
};

export const TournamentPlacesCell = (tournament: Tournament): React.ReactElement => {
  const { booked, capacity, unlimited } = getTournamentCapacitySummary(tournament);
  const percentage = capacity > 0 ? Math.min(100, Math.round((booked / capacity) * 100)) : 0;
  const progressTone = percentage >= 100 ? 'is-full' : percentage >= 80 ? 'is-nearly-full' : '';
  return (
    <td>
      <div className="utListCapacity">
        <strong>{unlimited ? `${booked} entered` : `${booked} / ${capacity}`}</strong>
        {unlimited ? <span>∞</span> : null}
      </div>
      {!unlimited || booked > 0 ? (
        <div className={`utListCapacityBar ${progressTone}`}>
          <i style={{ width: `${percentage}%` }} />
        </div>
      ) : null}
    </td>
  );
};

export const TournamentWaitingCell = (tournament: Tournament): React.ReactElement => {
  const waitingListSchoolsCount = Number(tournament.waitingListSchoolsCount || 0);
  return (
    <td className="utListCentered">
      <span className={waitingListSchoolsCount > 0 ? 'utListWaitingCount has-items' : 'utListWaitingCount'}>
        {waitingListSchoolsCount > 0 ? waitingListSchoolsCount : '–'}
      </span>
    </td>
  );
};

export const TournamentStatusCell = (tournament: Tournament): React.ReactElement => {
  const { booked, capacity, unlimited } = getTournamentCapacitySummary(tournament);
  const full = !unlimited && booked >= capacity;
  const completed = moment(tournament.endTime).isBefore(moment());
  const label = completed ? 'Completed' : full ? 'Full' : 'Open';
  const tone = completed ? 'is-neutral' : full ? 'is-warning' : 'is-success';
  return (
    <td>
      <span className={`utListBadge has-dot ${tone}`}>{label}</span>
    </td>
  );
};

export const TournamentPublishedCell = (tournament: Tournament): React.ReactElement => (
  <td>
    <span className={`utListBadge ${tournament.isPublishOnTheWebsite ? 'is-success' : 'is-neutral'}`}>
      {tournament.isPublishOnTheWebsite ? 'Published' : 'Not published'}
    </span>
  </td>
);

export const TournamentHighlightedCell = (tournament: Tournament): React.ReactElement => (
  <td>
    <span className={`utListBadge ${tournament.isPromoteTournament ? 'is-info' : 'is-neutral'}`}>
      {tournament.isPromoteTournament ? 'Highlighted' : 'Not highlighted'}
    </span>
  </td>
);

export const TournamentTagsCell = (tournament: Tournament): React.ReactElement => {
  const tags = tournament.tournamentTags || [];
  const first = tags[0];
  const remaining = tags.slice(1);
  const tagColor = first && (first.tagColor || '#64748b');
  const firstTagLabel = first
    ? first.tagName.length > 18
      ? `${first.tagName.slice(0, 18).replace(/\s+\S*$/, '').replace(/[\s-]+$/, '')}…`
      : first.tagName
    : '';
  return (
    <td>
      <div className="utListTags">
        {first ? (
          <span style={{ color: tagColor, backgroundColor: `${tagColor}1A` }} title={first.tagName}>
            <i style={{ backgroundColor: tagColor }} />
            <em>{firstTagLabel}</em>
          </span>
        ) : <span className="utListMuted">—</span>}
        {remaining.length ? <span className="utListTagsMore" title={remaining.map(tag => tag.tagName).join(', ')}>+{remaining.length}</span> : null}
      </div>
    </td>
  );
};
